import { inSpeedRun } from '@/lib/speedRun';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { firstVisitArm } from "@/lib/firstVisitArm";
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { X } from '@phosphor-icons/react';
import { track } from '@/lib/analytics';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';
import { playWelcomeVideo } from '@/components/TutorialVideoDialog';
import { competitionVisible, openCompetitionVideo, countdownTarget, timeLeft, useCompetition, useNow } from '@/lib/competition';
import {
  CHOOSER_EVENT, chooserIsDue, decideFirstVisit, onboardingEnabled, readMeta, readProgress, rememberIntent,
  saveMeta, setOnboardingActive, type Intent, type OnboardingUser,
} from '@/lib/onboarding';

/****************
 * "WHAT DO YOU WANT TO USE COPYFI FOR?" Mishka, 2026-10-21.
 *
 * The first thing a new account sees, before anything else: five answers,
 * three cards and two pills. The answer picks the welcome video and the guide
 * after it (lib/onboarding.ts). The design is the v2 screen of 2026-10-20
 * (copyfi-onboarding/choix.html), ported rather than pasted, because the three
 * welcome videos open on it and the app has to look like what they show.
 *
 * Opens by itself under the tutorial video's rule -- an account under a day
 * old that has not seen it -- and from the "Tutorial video" buttons for
 * everyone, which is how a choice is made again. Closing without choosing
 * starts nothing.
 ****************/

const CARDS: { intent: Intent; key: 'copy' | 'builder' | 'ai' }[] = [
  { intent: 'copy_trading', key: 'copy' },
  { intent: 'strategy_builder', key: 'builder' },
  { intent: 'ai_trading', key: 'ai' },
];

function CardIcon({ k }: { k: 'copy' | 'builder' | 'ai' }) {
  if (k === 'copy') return (
    <svg viewBox="0 0 24 24"><rect x="8" y="8" width="12" height="12" rx="3" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>
  );
  if (k === 'builder') return (
    <svg viewBox="0 0 24 24"><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></svg>
  );
  return (
    <svg viewBox="0 0 24 24"><path d="M12 3l1.8 4.7L18.5 9.5 13.8 11.3 12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></svg>
  );
}

/****************
 * THE SIXTH ANSWER: "I'M HERE FOR THE COMPETITION". Mishka, 2026-10-25.
 *
 * Orange, glowing, under the five, with the countdown in it. Not an Intent:
 * it starts no guide. It opens the competition video, which leads to the
 * competition page. Shown only while the competition is upcoming or open,
 * and to everyone (an admin sees it too, and is told on the page they cannot
 * enter).
 ****************/
function CompetitionAnswer({ onChoose }: { onChoose: () => void }) {
  const { t } = useTranslation('competition');
  const { data: info } = useCompetition();
  const now = useNow(30_000);
  // Admins only until it opens (Mishka, 2026-10-26).
  if (!info || info.phase === 'ended' || !competitionVisible(info)) return null;
  const left = timeLeft(countdownTarget(info), now);
  const time = t('countdown.short', { d: left.days, h: left.hours, m: String(left.minutes).padStart(2, '0') });
  return (
    <button type="button" className="ic-compete" onClick={onChoose}>
      <span className="ic-cpic" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8.5 20h7M10 17h4v3h-4z" /></svg>
      </span>
      <span className="ic-ctext">
        <span className="ic-ctitle">{t('chooser.button')}</span>
        <span className="ic-csub">{info.phase === 'upcoming' ? t('chooser.upcoming', { time }) : t('chooser.open', { time })}</span>
      </span>
      <span className="ic-go" aria-hidden="true">→</span>
    </button>
  );
}

export function IntentChooser() {
  const { t } = useTranslation('learn');
  const { user, isLoaded } = useUser();
  const [open, setOpen] = useState(false);
  const decidedRef = useRef(false);
  // Focus on the screen, not on a card: a ringed first card reads as a choice already made.
  const rootRef = useRef<HTMLDivElement>(null);
  const ouser = user as unknown as OnboardingUser | null | undefined;

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(CHOOSER_EVENT, onOpen);
    return () => window.removeEventListener(CHOOSER_EVENT, onOpen);
  }, []);

  /* A new account: the chooser has the first screen. Otherwise Quick Learn keeps it. */
  useEffect(() => {
    if (decidedRef.current || !isLoaded) return;
    decidedRef.current = true;
    if (!ouser || !onboardingEnabled() || isViewingAsSomeoneElse()) { decideFirstVisit(false); return; }
    // A speed run opens on the Dashboard (Guillaume 11-01), never on the chooser:
    // its own guide takes it from there (components/engagement/SpeedRunGuide.tsx).
    // "Claimed" keeps the Get Started gate from starting another guide over it.
    if (inSpeedRun()) { decideFirstVisit(true); return; }
    const meta = readMeta(ouser);
    rememberIntent(meta.intentLast);
    const p = readProgress(ouser.id);
    const guideToResume = !!p && !meta.guides?.[p.intent];
    if (chooserIsDue(ouser)) {
      // A sample of each first visit (lib/firstVisitArm.ts): half the new accounts
      // go to the guided Get Started instead of the chooser.
      const arm = firstVisitArm(ouser.id);
      track("first_visit_arm", arm);
      if (arm === "getstarted") { saveMeta(ouser, { chooserSeen: true }); decideFirstVisit(false); return; }
      saveMeta(ouser, { chooserSeen: true });
      setOpen(true);
      decideFirstVisit(true);
      return;
    }
    // Seen the chooser: it owns the first visit. Quick Learn came after the
    // general video, or not at all for the three goals (lib/onboarding.ts).
    decideFirstVisit(guideToResume || !!meta.chooserSeen);
  }, [isLoaded, ouser]);

  useEffect(() => {
    setOnboardingActive('chooser', open);
    if (open) window.setTimeout(() => rootRef.current?.focus({ preventScroll: true }), 50);
    return () => setOnboardingActive('chooser', false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const choose = (intent: Intent) => {
    if (ouser) {
      const before = readMeta(ouser);
      track('onboarding_intent_chosen', intent);
      if (before.intentLast && before.intentLast !== intent) {
        track('onboarding_intent_changed', intent, undefined, { from: before.intentLast, to: intent });
      }
      saveMeta(ouser, {
        intentFirst: before.intentFirst ?? intent,
        intentLast: intent,
        chooserSeen: true,
        chosenAt: new Date().toISOString(),
      });
    }
    setOpen(false);
    playWelcomeVideo(intent);
  };

  const chooseCompetition = () => {
    if (ouser) saveMeta(ouser, { chooserSeen: true });
    setOpen(false);
    openCompetitionVideo();
  };

  // The light follows the pointer on a card (the v2 screen's hover).
  const glow = (e: PointerEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    e.currentTarget.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  if (!open) return null;

  return (
    <div ref={rootRef} tabIndex={-1} className="ic-root" role="dialog" aria-modal="true" aria-labelledby="ic-title">
      <style>{CHOOSER_CSS}</style>
      <div className="ic-sky" aria-hidden="true" />
      <button type="button" className="ic-close" onClick={() => setOpen(false)} aria-label={t('guide.chooser.close')}>
        <X size={18} />
      </button>
      <main className="ic-stage">
        <h1 id="ic-title" className="ic-h1">{t('guide.chooser.title')}</h1>

        <div className="ic-duo">
          <button type="button" className="ic-pill" onClick={() => choose('dont_know')}>
            <span className="ic-pic">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.6" /><path d="M12 17h.01" /></svg>
            </span>
            {t('guide.chooser.dontKnow')}
          </button>
          <button type="button" className="ic-pill" onClick={() => choose('want_everything')}>
            <span className="ic-pic">
              <svg viewBox="0 0 24 24"><rect x="4" y="4" width="7" height="7" rx="2" /><rect x="13" y="4" width="7" height="7" rx="2" /><rect x="4" y="13" width="7" height="7" rx="2" /><rect x="13" y="13" width="7" height="7" rx="2" /></svg>
            </span>
            {t('guide.chooser.everything')}
          </button>
        </div>

        <div className="ic-cards" role="group" aria-label={t('guide.chooser.aria')}>
          {CARDS.map(({ intent, key }) => (
            <button
              key={intent}
              type="button"
              className="ic-card"
              onPointerMove={glow}
              onClick={() => choose(intent)}
            >
              <span className="ic-icon"><CardIcon k={key} /></span>
              <h2>{t(`guide.chooser.${key}.title`)}</h2>
              <p>{t(`guide.chooser.${key}.body`)}</p>
              <span className="ic-go" aria-hidden="true">→</span>
            </button>
          ))}
        </div>

        <CompetitionAnswer onChoose={chooseCompetition} />
      </main>
    </div>
  );
}

/* The v2 screen's look: #08090F ground, a violet arc above, a blue glow below, glass cards; hover lifts and rings #1380EC. */
const CHOOSER_CSS = `
.ic-root {
  position: fixed; inset: 0; z-index: 70; overflow-y: auto; outline: none;
  background: #08090f; color: #f4f5fb; -webkit-font-smoothing: antialiased;
}
.ic-sky {
  position: fixed; inset: 0; pointer-events: none;
  background:
    radial-gradient(ellipse 55% 42% at 50% -6%, rgba(92,72,255,.42), rgba(92,72,255,.12) 45%, transparent 75%),
    radial-gradient(ellipse 42% 28% at 50% 110%, rgba(40,110,255,.28), transparent 72%);
}
.ic-close {
  position: fixed; top: 16px; right: 16px; z-index: 2; width: 38px; height: 38px; border-radius: 50%;
  display: grid; place-items: center; cursor: pointer; color: rgba(228,231,246,.75);
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.11);
}
.ic-close:hover { color: #fff; background: rgba(255,255,255,.1); }
.ic-stage {
  position: relative; z-index: 1; min-height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 56px 16px 40px; gap: clamp(26px, 4vh, 44px);
}
.ic-h1 {
  margin: 0; text-align: center; font-weight: 600; letter-spacing: -.035em; line-height: 1.05;
  font-size: clamp(28px, 4.2vw, 60px); text-wrap: balance; max-width: 16ch;
}
.ic-duo { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; width: min(560px, 100%); }
.ic-pill {
  appearance: none; cursor: pointer; font: inherit; color: #f4f5fb;
  display: inline-flex; align-items: center; gap: 12px; flex: 1 1 220px;
  height: 64px; padding: 0 26px 0 14px; border-radius: 18px;
  background: rgba(255,255,255,.055); border: 1px solid rgba(255,255,255,.11);
  backdrop-filter: blur(20px) saturate(140%); -webkit-backdrop-filter: blur(20px) saturate(140%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.10), 0 12px 32px rgba(0,0,0,.28);
  font-size: 18px; font-weight: 500;
  transition: transform .25s ease, border-color .25s ease, background .25s ease, box-shadow .25s ease;
}
.ic-pic {
  width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center; flex: none;
  background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.13); transition: background .25s ease;
}
.ic-pic svg, .ic-icon svg { stroke: #e9e6ff; fill: none; stroke-linecap: round; stroke-linejoin: round; }
.ic-pic svg { width: 20px; height: 20px; stroke-width: 1.8; }
.ic-pill:hover, .ic-pill:focus-visible {
  transform: translateY(-2px); border-color: #1380ec; background: rgba(255,255,255,.085);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.16), 0 0 0 1px rgba(19,128,236,.45), 0 18px 50px rgba(19,128,236,.25);
}
.ic-pill:hover .ic-pic, .ic-pill:focus-visible .ic-pic { background: linear-gradient(135deg, #8b7bff, #1380ec); border-color: transparent; }

.ic-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; width: min(1080px, 100%); }
.ic-card {
  position: relative; appearance: none; cursor: pointer; text-align: start; font: inherit; color: #f4f5fb;
  padding: 30px 28px 26px; border-radius: 24px; min-height: 250px;
  display: flex; flex-direction: column; gap: 12px; overflow: hidden;
  background: linear-gradient(165deg, rgba(255,255,255,.075), rgba(255,255,255,.02));
  border: 1px solid rgba(255,255,255,.11);
  backdrop-filter: blur(26px) saturate(150%); -webkit-backdrop-filter: blur(26px) saturate(150%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.13), 0 22px 56px rgba(0,0,0,.34);
  transition: transform .35s cubic-bezier(.2,.8,.2,1), border-color .3s ease, box-shadow .3s ease;
}
.ic-card::before {
  content: ""; position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
  background: radial-gradient(380px circle at var(--mx, 50%) var(--my, 0%), rgba(120,170,255,.18), transparent 55%);
  opacity: 0; transition: opacity .3s ease;
}
.ic-card:hover::before, .ic-card:focus-visible::before { opacity: 1; }
.ic-card:hover, .ic-card:focus-visible {
  transform: translateY(-6px); border-color: #1380ec; outline: none;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.18), 0 0 0 1px rgba(19,128,236,.5), 0 30px 80px rgba(19,128,236,.28);
}
.ic-pill:focus-visible { outline: none; }
.ic-icon {
  width: 52px; height: 52px; border-radius: 15px; display: grid; place-items: center;
  background: linear-gradient(140deg, rgba(139,123,255,.28), rgba(66,133,255,.2)); border: 1px solid rgba(255,255,255,.15);
}
.ic-icon svg { width: 26px; height: 26px; stroke-width: 1.7; }
.ic-card h2 { margin: 8px 0 0; font-size: 26px; font-weight: 600; letter-spacing: -.02em; }
.ic-card p { margin: 0; color: rgba(228,231,246,.66); font-size: 16px; line-height: 1.5; max-width: 28ch; }
.ic-go {
  position: absolute; inset-inline-end: 22px; bottom: 22px; width: 38px; height: 38px; border-radius: 50%;
  display: grid; place-items: center; border: 1px solid rgba(255,255,255,.11); background: rgba(255,255,255,.05);
  transition: background .3s ease, transform .3s ease;
}
.ic-card:hover .ic-go, .ic-card:focus-visible .ic-go { background: linear-gradient(135deg, #8b7bff, #1380ec); border-color: transparent; transform: translateX(3px); }
[dir="rtl"] .ic-go { transform: scaleX(-1); }

/* The competition's answer: orange, with a slow glow (Mishka, 2026-10-25). */
.ic-compete {
  position: relative; appearance: none; cursor: pointer; font: inherit; color: #fff; text-align: start;
  display: flex; align-items: center; gap: 16px; width: min(1080px, 100%);
  padding: 18px 70px 18px 18px; border-radius: 22px;
  background: linear-gradient(120deg, #ff7a1a, #ff9a3c 55%, #ff6a00);
  border: 1px solid rgba(255,200,150,.55);
  box-shadow: 0 0 0 1px rgba(255,122,26,.35), 0 0 34px rgba(255,122,26,.55), 0 18px 50px rgba(255,106,0,.35);
  animation: ic-glow 2.6s ease-in-out infinite;
  transition: transform .3s cubic-bezier(.2,.8,.2,1);
}
.ic-compete:hover, .ic-compete:focus-visible { transform: translateY(-3px); outline: none; }
.ic-cpic { width: 48px; height: 48px; border-radius: 14px; display: grid; place-items: center; flex: none; background: rgba(0,0,0,.18); border: 1px solid rgba(255,255,255,.3); }
.ic-cpic svg { width: 26px; height: 26px; stroke: #fff; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.ic-ctext { display: flex; flex-direction: column; gap: 4px; }
.ic-ctitle { font-size: 22px; font-weight: 650; letter-spacing: -.02em; }
.ic-csub { font-size: 14px; color: rgba(255,255,255,.88); font-variant-numeric: tabular-nums; }
.ic-compete .ic-go { background: rgba(0,0,0,.2); border-color: rgba(255,255,255,.35); }
@keyframes ic-glow {
  0%, 100% { box-shadow: 0 0 0 1px rgba(255,122,26,.35), 0 0 26px rgba(255,122,26,.45), 0 18px 50px rgba(255,106,0,.3); }
  50% { box-shadow: 0 0 0 1px rgba(255,160,90,.6), 0 0 48px rgba(255,122,26,.8), 0 18px 60px rgba(255,106,0,.45); }
}

@media (max-width: 860px) {
  .ic-compete { padding-inline-end: 64px; }
  .ic-ctitle { font-size: 18px; }
  .ic-cards { grid-template-columns: 1fr; }
  .ic-card { min-height: 0; padding-inline-end: 70px; }
  .ic-card h2 { font-size: 22px; }
}
@media (prefers-reduced-motion: reduce) { .ic-card, .ic-pill, .ic-compete { transition: none; animation: none; } .ic-card:hover, .ic-pill:hover, .ic-compete:hover { transform: none; } }
`;
